import {
  Check,
  ChevronDown,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { Button, DropdownMenu } from 'slab'

export type ActionIconMap = Record<string, ReactNode>
export type ActionLabelMap = Record<string, string>

export type AASMActionControlsProps<T extends string = string> = {
  /** Available actions from the AASM state machine */
  availableActions: T[] | undefined
  /** Custom empty state label */
  emptyStateLabel?: string
  /** Function to filter actions before sorting (optional) */
  filterActions?: (actions: T[]) => T[]
  /** Function to get icon for an action (optional, uses default icons if not provided) */
  getActionIcon?: (action: T | undefined, isPending: boolean) => ReactNode
  /** Function to get label for an action */
  getActionLabel: (action: T) => string
  /** Whether the mutation is pending */
  isPending: boolean
  /** Callback when action is executed */
  onActionExecute: (action: T) => void
  /** Additional UI elements to render (e.g., dialogs, popovers) */
  renderAdditionalControls?: ReactNode
  /** Custom action button component */
  renderActionButton?: (props: {
    selectedAction: T | undefined
    isPending: boolean
    handleExecuteAction: () => void
  }) => ReactNode
  /** Additional elements to render before the dropdown (e.g., warning icons) */
  renderBeforeDropdown?: ReactNode
  /** Custom empty state component */
  renderEmptyState?: () => ReactNode
  /** Custom spacing between elements */
  spacing?: 'space-x-1' | 'space-x-2'
  /** Function to sort actions (optional, defaults to no sorting) */
  sortActions?: (actions: T[]) => T[]
  /** Whether to use portal for dropdown */
  withPortal?: boolean
}

/**
 * Generic AASM Action Controls component
 * Handles state management, auto-selection, and rendering of action dropdown + execute button
 */
const AASMActionControls = <T extends string = string>({
  availableActions,
  emptyStateLabel = 'No Actions Available',
  filterActions,
  getActionIcon,
  getActionLabel,
  isPending,
  onActionExecute,
  renderAdditionalControls,
  renderActionButton,
  renderBeforeDropdown,
  renderEmptyState,
  spacing = 'space-x-2',
  sortActions,
  withPortal,
}: AASMActionControlsProps<T>) => {
  const [selectedAction, setSelectedAction] = useState<T | undefined>()

  // Filter and sort actions
  const processedActions = useMemo(() => {
    if (!availableActions || availableActions.length === 0) return []
    let actions = [...availableActions]
    if (filterActions) {
      actions = filterActions(actions)
    }
    if (sortActions) {
      actions = sortActions(actions)
    }
    return actions
  }, [availableActions, filterActions, sortActions])

  // Track previous available actions to detect real changes
  const prevAvailableActionsRef = useRef<T[]>([])

  // Auto-select first available action ONLY when available actions actually change
  useEffect(() => {
    const prevActions = prevAvailableActionsRef.current
    const currentActions = processedActions

    // Check if the available actions actually changed (not just a re-render)
    const actionsChanged =
      prevActions.length !== currentActions.length ||
      prevActions.some((action, index) => action !== currentActions[index])

    if (actionsChanged) {
      // Actions changed - update the ref and reset to first action
      prevAvailableActionsRef.current = currentActions

      if (processedActions && processedActions.length > 0) {
        setSelectedAction(processedActions[0])
      } else {
        setSelectedAction(undefined)
      }
    }
  }, [processedActions])

  const hasActions = processedActions.length > 0

  const handleExecuteAction = () => {
    if (!selectedAction) return
    onActionExecute(selectedAction)
  }

  // Default icon mapping
  const getDefaultActionIcon = (action: T | undefined, isPending: boolean): ReactNode => {
    if (isPending) return <Loader2 className='w-4 h-4 animate-spin' />

    if (!action) return <Play className='w-4 h-4' />

    // Common action icon mappings
    const actionLower = action.toLowerCase()
    if (actionLower.includes('play') || actionLower.includes('start') || actionLower.includes('resume') || actionLower.includes('execute')) {
      return <Play className='w-4 h-4' />
    }
    if (actionLower.includes('pause')) {
      return <Pause className='w-4 h-4' />
    }
    if (actionLower.includes('cancel') || actionLower.includes('fail') || actionLower.includes('mark_failed')) {
      return <X className='w-4 h-4' />
    }
    if (actionLower.includes('complete') || actionLower.includes('confirm')) {
      return <Check className='w-4 h-4' />
    }
    if (actionLower.includes('retry') || actionLower.includes('rollback') || actionLower.includes('prepare')) {
      return <RotateCcw className='w-4 h-4' />
    }

    return <Play className='w-4 h-4' />
  }

  const actionIcon = getActionIcon
    ? getActionIcon(selectedAction, isPending)
    : getDefaultActionIcon(selectedAction, isPending)

  // Default action button
  const DefaultActionButton = () => (
    <Button
      size='small'
      variant='primary'
      onClick={handleExecuteAction}
      disabled={!selectedAction || isPending || !hasActions}
    >
      {actionIcon}
    </Button>
  )

  // Custom empty state
  if (!hasActions) {
    if (renderEmptyState) {
      return (
        <>
          {renderEmptyState()}
          {renderAdditionalControls}
        </>
      )
    }

    return (
      <div className={`flex items-center ${spacing}`}>
        {renderBeforeDropdown}
        <Button variant='secondary' size='small' disabled>
          {emptyStateLabel}
        </Button>
        {renderActionButton
          ? (
              renderActionButton({
                selectedAction: undefined,
                isPending: false,
                handleExecuteAction: () => {},
              })
            )
          : (
            <DefaultActionButton />
            )}
        {renderAdditionalControls}
      </div>
    )
  }

  // Default action button renderer
  const ActionButton = renderActionButton
    ? (
        renderActionButton({
          selectedAction,
          isPending,
          handleExecuteAction,
        })
      )
    : (
      <DefaultActionButton />
      )

  return (
    <>
      <div className={`flex items-center ${spacing}`}>
        {renderBeforeDropdown}
        {hasActions && (
          <DropdownMenu
            withPortal={withPortal}
            trigger={
              <Button variant='secondary' size='small' rightIcon={ChevronDown}>
                {selectedAction ? getActionLabel(selectedAction) : 'Select Action'}
              </Button>
            }
          >
            {processedActions.map((action) => (
              <DropdownMenu.Item
                key={action}
                onSelect={() => setSelectedAction(action)}
              >
                {getActionLabel(action)}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        )}

        {ActionButton}
        {renderAdditionalControls}
      </div>
    </>
  )
}

export default AASMActionControls
